import { clone, find, get } from 'lodash';

export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

interface EdgeNode {
  node: unknown;
}

// biome-ignore lint/suspicious/noExplicitAny: cleanPayload is a generic utility that extracts nodes from GraphQL relay-style responses. It must accept any object structure with edges/nodes.
export const cleanPayload = <T = unknown>(payload: any): T[] => {
  // remove edges and node from payload
  return clone(get(find(payload, 'edges'), 'edges', []).map((node: EdgeNode) => get(node, 'node')));
};

// biome-ignore lint/suspicious/noExplicitAny: cleanEdgesCleanNode works with various GraphQL edge structures
export const cleanEdgesCleanNode = <T = unknown>(payload: any): T[] => {
  return get(payload, 'edges', []).map((node: EdgeNode) => get(node, 'node'));
};

interface MetadataFieldPayload {
  definition?: {
    name?: string;
  };
  [key: string]: unknown;
}

// biome-ignore lint/suspicious/noExplicitAny: Works with various GraphQL metadata response structures
export const getMetadataField = (payload: any, fieldName: string): MetadataFieldPayload | undefined => {
  const cleanedPayload = cleanPayload<MetadataFieldPayload>(payload);
  return cleanedPayload.find((mf) => mf.definition?.name === fieldName);
};

// biome-ignore lint/suspicious/noExplicitAny: Works with various GraphQL metadata response structures
export const getMetadataFieldValue = (payload: any, fieldName: string): string => {
  const field = getMetadataField(payload, fieldName);
  const values = cleanPayload<{ data?: { data?: string } }>(field);
  return values.map((val) => get(val, 'data.data', '')).join(', ');
};

export const getMetadata = (payload: MetadataFieldPayload[], fieldName: string): MetadataFieldPayload | undefined => {
  // const cleanedPayload = cleanPayload(payload);
  return payload.find((mf) => mf.definition?.name === fieldName);
};

export const getFirstNestedWithSaneDefault = <T>(ob: Record<string, T> | null | undefined, def: T = {} as T): T => {
  const result = ob ? Object.values(ob)[0] : def;
  return result;
};

interface MetadataField {
  definition?: { slug?: string };
  values?: Array<{ data?: { data?: string } }>;
}

export const getMetadataValue = (
  payload: Record<string, unknown> | null | undefined,
  slug: string,
): string | undefined => {
  const fields = getFirstNestedWithSaneDefault<MetadataField[]>(
    payload as Record<string, MetadataField[]> | null | undefined,
    [],
  );
  const field = fields?.find((mf: MetadataField) => mf.definition?.slug === slug);
  return field?.values?.map((val) => get(val, 'data.data', '')).join(', ');
};

interface MetadataFieldWithName extends MetadataField {
  definition?: { slug?: string; name?: string };
}

// Returns the client-configured display name of a FieldDef by slug (e.g. "Archive Group"
// when the client renamed "Media Group"). Falls back to undefined when not found.
export const getMetadataFieldLabel = (
  payload: Record<string, unknown> | null | undefined,
  slug: string,
): string | undefined => {
  const fields = getFirstNestedWithSaneDefault<MetadataFieldWithName[]>(
    payload as Record<string, MetadataFieldWithName[]> | null | undefined,
    [],
  );
  const field = fields?.find((mf: MetadataFieldWithName) => mf.definition?.slug === slug);
  return field?.definition?.name;
};

// biome-ignore lint/suspicious/noExplicitAny: Works with various GraphQL configuration response structures
export const getConfig = (payload: any, key: string, val: unknown): Record<string, unknown> | undefined => {
  const cleanedPayload = cleanPayload<Record<string, unknown>>(payload) || [];
  return cleanedPayload.filter((x) => x[key] === val)[0];
};
